import React, { FC, useMemo } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Badge,
  CircularProgress,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useGuardianMessagingContext } from '../../../../../contexts/guardianMessaging.context';
import {
  ConversationSummary,
  MessageType,
} from '../../../../../../../../apis/messaging/messaging.interfaces';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'src/hooks/useTranslationHook';

const GuardianConversationList: FC = () => {
  const { translateText } = useTranslation();
  const {
    state,
    setSelectedConversation,
    setSelectedReceiver,
    setSelectedClass,
    setConversationFilter,
    setSearchQuery,
  } = useGuardianMessagingContext();

  const filteredConversations = useMemo(() => {
    let filtered = [...state.conversations];

    // Apply filter
    if (state.conversationFilter === 'unread') {
      filtered = filtered.filter((conv) => (conv.unread_count ?? 0) > 0);
    } else if (state.conversationFilter === 'groups') {
      filtered = filtered.filter((conv) => conv.type === MessageType.BROADCAST);
    }

    // Apply search
    if (state.searchQuery.trim()) {
      const query = state.searchQuery.toLowerCase();
      filtered = filtered.filter((conv) => {
        if (conv.type === MessageType.DIRECT_MESSAGE && conv.other_user) {
          const name =
            `${conv.other_user.first_name} ${conv.other_user.last_name}`.toLowerCase();
          return name.includes(query);
        } else if (conv.type === MessageType.BROADCAST && conv.class_info) {
          const className = conv.class_info.level_name?.toLowerCase() || '';
          return className.includes(query);
        }
        return false;
      });
    }

    // Sort by last activity
    return filtered.sort(
      (a, b) =>
        new Date(b.last_activity || 0).getTime() -
        new Date(a.last_activity || 0).getTime()
    );
  }, [state.conversations, state.conversationFilter, state.searchQuery]);

  const handleConversationClick = (conversation: ConversationSummary) => {
    setSelectedConversation(conversation.conversation_id || null);
    if (
      conversation.type === MessageType.DIRECT_MESSAGE &&
      conversation.other_user
    ) {
      setSelectedReceiver(conversation.other_user._id);
      setSelectedClass(null);
    } else if (
      conversation.type === MessageType.BROADCAST &&
      conversation.class_info
    ) {
      setSelectedClass(conversation.class_info._id);
      setSelectedReceiver(null);
    }
  };

  const getConversationName = (conversation: ConversationSummary): string => {
    if (
      conversation.type === MessageType.DIRECT_MESSAGE &&
      conversation.other_user
    ) {
      return `${conversation.other_user.first_name} ${conversation.other_user.last_name}`;
    } else if (
      conversation.type === MessageType.BROADCAST &&
      conversation.class_info
    ) {
      return `${conversation.class_info.level_name}${conversation.section ? ` - ${conversation.section}` : ''}`;
    }
    return translateText('communications.messaging.unknown');
  };

  const getConversationAvatar = (conversation: ConversationSummary): string => {
    if (
      conversation.type === MessageType.DIRECT_MESSAGE &&
      conversation.other_user?.profile_picture
    ) {
      return conversation.other_user.profile_picture;
    }
    return '';
  };

  const getLastMessagePreview = (conversation: ConversationSummary): string => {
    if (conversation.last_message) {
      return conversation.last_message.content;
    }
    return translateText('communications.messaging.noMessagesYet');
  };

  const getTimeAgo = (date: string | Date): string => {
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch {
      return '';
    }
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
      }}
      data-testid="guardian-conversation-list"
    >
      {/* Search Bar */}
      <Box sx={{ p: { xs: 1, sm: 2 }, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          fullWidth
          size="small"
          placeholder={translateText(
            'communications.messaging.searchOrStartChat'
          )}
          value={state.searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: { xs: '16px', sm: '14px' },
              minHeight: { xs: '44px', sm: 'auto' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Filter Buttons */}
      <Box sx={{ p: { xs: 1, sm: 1.5 }, borderBottom: 1, borderColor: 'divider' }}>
        <ToggleButtonGroup
          value={state.conversationFilter}
          exclusive
          onChange={(_, value) => value && setConversationFilter(value)}
          size="small"
          fullWidth
          sx={{
            '& .MuiToggleButton-root': {
              minHeight: { xs: '44px', sm: 'auto' },
              fontSize: { xs: '16px', sm: '14px' },
            },
          }}
        >
          <ToggleButton value="all">
            {translateText('communications.messaging.all')}
          </ToggleButton>
          <ToggleButton value="unread">
            {translateText('communications.messaging.unread')}
          </ToggleButton>
          <ToggleButton value="groups">
            {translateText('communications.messaging.groups')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Conversation List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {state.isLoadingConversations ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : filteredConversations.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {translateText('communications.messaging.noConversations')}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {filteredConversations.map((conversation) => {
              const isSelected =
                state.selectedConversationId === conversation.conversation_id;
              return (
                <ListItem key={conversation.conversation_id} disablePadding>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => handleConversationClick(conversation)}
                    sx={{
                      borderRadius: 1,
                      mx: { xs: 0.5, sm: 1 },
                      minHeight: { xs: '64px', sm: 'auto' },
                      '&.Mui-selected': {
                        backgroundColor: '#EEF0FF',
                        '&:hover': {
                          backgroundColor: '#E1E4FF',
                        },
                        '& .MuiListItemText-primary': {
                          color: 'primary.main',
                        },
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Badge
                        badgeContent={conversation.unread_count}
                        color="primary"
                        invisible={conversation.unread_count === 0}
                      >
                        <Avatar src={getConversationAvatar(conversation)}>
                          {getConversationName(conversation)
                            .charAt(0)
                            .toUpperCase()}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight:
                                (conversation.unread_count ?? 0) > 0 ? 600 : 400,
                            }}
                          >
                            {getConversationName(conversation)}
                          </Typography>
                          {conversation.last_message && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {getTimeAgo(conversation.last_message.created_at)}
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontWeight:
                              (conversation.unread_count ?? 0) > 0 ? 500 : 400,
                          }}
                        >
                          {getLastMessagePreview(conversation)}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default GuardianConversationList;
